import StringUtils from "../utils/StringUtils.js";
import StorageService from "../services/StorageService.js";
import MapInteraction from "../utils/MapInteraction.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import BaseGame from "./BaseGame.js";

/** Temps laissé au joueur pour lire la réponse révélée après une erreur, en ms. */
const REVEAL_DELAY = 1500;

/**
 * US States, mode Hard : clic sur la carte puis saisie du nom tapé. Copie de
 * MapNerdHard.js sans la logique de scope/continent.
 */
export default class UsStatesHard extends BaseGame {
    constructor() {
        super("usstateshard", ".us-states-hard", {
            gameNameKey: "home.usStates",
            modeLabelKey: "home.hard",
            mode: "hard",
            shareEmoji: "🟥",
            slug: "us-states-hard"
        });
        if (!this.page) return;

        this.mapContainer = this.page.querySelector(".map-container");
        this.map = this.mapContainer.querySelector("svg");
        this.countryToGuess = this.page.querySelector(".country-to-guess");
        this.countryToGuessName = this.countryToGuess.querySelector(".name");
        this.countryToGuessFlag = this.countryToGuess.querySelector(".flag-container");
        this.inputContainer = this.countryToGuess.querySelector(".input-container");
        this.answerInput = this.inputContainer.querySelector(".answer-input");
        this.countriesElements = this.map.querySelectorAll("path");

        this.mapInteraction = new MapInteraction(this.mapContainer, this.map);
        this.buildEvents();
        this.infiniteButton();
        this.init();

        this.isInfinite = false;
        this.selectionMode = true;
    }

    init() {
        this.initCountries(geoNerdApp.usStates);
        this.startClock();
        this.newProposal();
        this.reloadButton();
        this.bindRestartButton();
        this.initShare();
        this.updateHeartsUI();
        this.updateProgress();
        this.maybeShowHelp();
    }

    initCountries(defaultList) {
        super.initCountries(defaultList);
        geoNerdApp.usStates.forEach(s => {
            const path = this.map.querySelector(`#${s.code}`);
            if (!path) return;
            path.classList.add("onu");
            if (!this.countriesLeft.find(left => left.code === s.code)) {
                path.classList.add("guessed");
            }
        });
    }

    /** Popup "Comment jouer ?" une fois par visite, comme MapNerdController.maybeShowHelp(). */
    maybeShowHelp() {
        const hideHelpKey = `geonerd.${this.gameId}.hideHelp`;
        if (localStorage.getItem(hideHelpKey) === "true") return;
        const popupOverlay = this.page.querySelector(".help-popup-overlay");
        if (!popupOverlay) return;

        const description = popupOverlay.querySelector(".help-description");
        description.innerHTML = geoNerdApp.languageManager.t("game.usStatesHardHelp");
        popupOverlay.classList.add("show");

        const closeBtn = popupOverlay.querySelector(".btn-close-help");
        const checkbox = popupOverlay.querySelector("#dont-show-help");
        closeBtn.addEventListener("click", () => {
            if (checkbox && checkbox.checked) localStorage.setItem(hideHelpKey, "true");
            popupOverlay.classList.remove("show");
        }, { once: true });
    }

    buildEvents() {
        this.countriesElements.forEach(country => {
            country.addEventListener("click", () => {
                if (!this.mapInteraction.dragged && this.canGuess && this.selectionMode && country.classList.contains("onu") && !country.classList.contains("guessed")) {
                    this.handleMapClick(country.id);
                }
            });
        });
        this.answerInput.addEventListener("input", () => {
            if (this.countryToGuessFlag.innerHTML !== "") {
                this.countryToGuessFlag.innerHTML = "";
                this.countryToGuessFlag.classList.remove("show");
            }
        });
        this.answerInput.addEventListener("keypress", e => {
            if (e.key === "Enter") this.validateTyping(this.answerInput.value);
        });
    }

    handleMapClick(countryCode) {
        const countryData = geoNerdApp.usStates.find(c => c.code === countryCode);
        if (!countryData) return;
        if (this.rightAnswer) {
            const prevPath = this.map.querySelector(`#${this.rightAnswer.code}`);
            if (prevPath) prevPath.classList.remove("selected");
        }
        this.rightAnswer = countryData;
        this.countryToGuessFlag.innerHTML = "";
        this.countryToGuessFlag.classList.remove("show");
        this.countryToGuessName.innerHTML = "";
        this.inputContainer.classList.add("active");
        this.countryToGuess.classList.add("typing-mode");
        this.answerInput.focus();
        const path = this.map.querySelector(`#${countryCode}`);
        if (path) path.classList.add("selected");
    }

    validateTyping(answer) {
        if (!this.canGuess || !this.rightAnswer) return;
        if (!answer || !answer.trim()) return;
        this.guessResult(StringUtils.matchesAnswer(answer, this.rightAnswer, geoNerdApp.usStates));
    }

    guessResult(isCorrect) {
        if (this.destroyed) return;
        this.canGuess = false;
        this.selectionMode = true;

        if (!isCorrect) {
            this.recordError({
                countryName: this.rightAnswer.name,
                correctCode: this.rightAnswer.code,
                guessedText: this.answerInput.value
            });
        }

        if (isCorrect) {
            SoundManager.correct();
            FeedbackEffects.confetti(this.countryToGuess);
            this.countryToGuess.classList.add("valid");
            this.countryToGuessFlag.innerHTML = `<div class="flag" style="background-image: url(${getFlagPath(`us-${this.rightAnswer.code}`)})"></div>`;
            this.countryToGuessFlag.classList.add("show");
            const path = this.map.querySelector(`#${this.rightAnswer.code}`);
            if (path) {
                path.classList.remove("selected");
                path.classList.add("guessed");
            }
            this.countriesLeft = this.countriesLeft.filter(elem => elem.code !== this.rightAnswer.code);
            StorageService.save(`${this.gameId}.current`, { found: true });
            this.updateStorage();
            this.updateProgress();

            this.inputContainer.classList.remove("active");
            this.countryToGuess.classList.remove("typing-mode");
            this.answerInput.value = "";
        } else {
            SoundManager.wrong();
            FeedbackEffects.shake(this.countryToGuess);
            this.countryToGuess.classList.add("invalid");
            const path = this.map.querySelector(`#${this.rightAnswer.code}`);
            if (path) {
                path.classList.remove("selected");
                path.classList.add("invalid-flash");
                setTimeout(() => path.classList.remove("invalid-flash"), 500);
            }
            this.revealAnswerName();
        }

        const delay = isCorrect ? 600 : REVEAL_DELAY;

        if (this.isInfinite || isCorrect) {
            setTimeout(() => {
                this.newProposal();
                this.canGuess = true;
            }, delay);
        } else {
            this.updateLife(true);
            if (this.lifes > 0) {
                setTimeout(() => {
                    this.newProposal();
                    this.canGuess = true;
                }, delay);
            }
        }
    }

    /** Affiche le drapeau et le nom de l'État que le joueur n'a pas su nommer. */
    revealAnswerName() {
        const lang = window.geoNerdApp.languageManager;
        this.countryToGuessFlag.innerHTML = `<div class="flag" style="background-image: url(${getFlagPath(`us-${this.rightAnswer.code}`)})"></div>`;
        this.countryToGuessFlag.classList.add("show");
        this.countryToGuessName.innerHTML = `${lang.t("game.answerWas")} <strong>${this.rightAnswer.name}</strong>`;
        this.inputContainer.classList.remove("active");
        this.countryToGuess.classList.remove("typing-mode");
    }

    newProposal() {
        if (this.destroyed) return;
        if (this.countriesLeft.length === 0) {
            this.finishGame();
            return;
        }
        gsap.to(this.countryToGuess, { opacity: 1 });
        this.countryToGuess.classList.remove("valid", "invalid", "typing-mode");
        this.inputContainer.classList.remove("active");
        this.answerInput.value = "";
        this.countryToGuessName.innerHTML = window.geoNerdApp.languageManager.t('game.chooseState');
        this.countryToGuessFlag.innerHTML = "";
        this.countryToGuessFlag.classList.remove("show");
        this.rightAnswer = null;
        this.selectionMode = true;
    }

    updateLife(decrease) {
        if (this.isInfinite) {
            this.updateProgress();
            return;
        }
        if (decrease) {
            this.lifes--;
            StorageService.save(`${this.gameId}.lifes`, this.lifes);
        }
        this.updateHeartsUI();
        if (this.lifes <= 0) {
            SoundManager.gameOver();
            this.lifes = 0;
            StorageService.save(`${this.gameId}.lifes`, 0);
            this.recordLoss();

            const correctPath = this.map.querySelector(`#${this.rightAnswer.code}`);
            if (correctPath) {
                correctPath.classList.add("correct-answer");
                this.mapInteraction.zoomToElement(correctPath, null, 2);
            }

            this.revealRecapAfterDelay(() => {
                this.renderEndGameStats(this.looseMessage);
                this.renderErrors(this.looseMessage);
                this.looseMessage.classList.add("show");
                this.page.querySelector(".progress-container").style.display = "none";
                if (correctPath) correctPath.classList.remove("correct-answer");
            });
        }
        this.updateProgress();
    }

    /**
     * Défère `reveal` de 2 s, le temps de voir où était l'État sur la carte
     * avant de passer au récap. Le bouton "Recommencer" disparaît dans l'instant :
     * laissé visible, il restait cliquable pendant ce délai et réinitialisait la
     * partie au lieu de montrer le récap. La sauvegarde (best/stats) a déjà eu
     * lieu au moment de cet appel.
     */
    revealRecapAfterDelay(reveal) {
        const restartBtn = this.page.querySelector(".btn-restart-ingame");
        if (restartBtn) restartBtn.style.display = "none";
        setTimeout(reveal, 2000);
    }

    infiniteButton() {
        const btn = this.page.querySelector(".btn.infinite"), quitBtn = this.page.querySelector(".btn.infinite-quit");
        if (btn) {
            btn.addEventListener("click", () => {
                this.isInfinite = true;
                this.looseMessage.classList.remove("show");
                this.page.querySelector(".life-remaining").style.display = "none";
                this.page.querySelector(".btn-restart-ingame").style.display = "none";
                this.page.querySelector(".progress-container").style.display = "flex";
                this.page.querySelector(".progress-container").style.pointerEvents = "all";
                this.page.querySelector(".infinite-actions").style.display = "flex";
                if (!this.page.querySelector(".infinite-label-added")) {
                    this.page.querySelector(".progress").insertAdjacentHTML("beforeend", `<span class="infinite-label-added">&nbsp;-&nbsp;${window.geoNerdApp.languageManager.t('game.infiniteMode')}</span>`);
                }
                this.newProposal();
                this.canGuess = true;
            });
        }
        if (quitBtn) {
            quitBtn.addEventListener("click", () => {
                window.location.href = window.geoNerdApp.languageManager.currentLang === "en" ? "/en/" : "/";
            });
        }
    }

    renderErrorItem(error) {
        return `
            <div class="error-item">
                <div class="error-flag correct">
                    <img src="${getFlagPath(`us-${error.correctCode}`)}" alt="${error.countryName}">
                </div>
                <div class="error-details">
                    <div class="error-country">${error.countryName}</div>
                    <div class="error-answer-text wrong">${error.guessedText}</div>
                </div>
            </div>
        `;
    }

    finishGame() {
        SoundManager.victory();
        this.mapInteraction.destroy();
        this.recordWin();
        this.updateProgress();
        this.revealRecapAfterDelay(() => this.showWinMessage());
    }

    teardown() {
        this.destroyed = true;
        this.canGuess = false;
        if (this.mapInteraction) this.mapInteraction.destroy();
    }
}
